import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2, CheckCircle2, FileText, Eraser, AlertTriangle, ShieldCheck,
  Download, Upload, X as XIcon, Paperclip,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  kbis:      'Extrait Kbis',
  urssaf:    'Attestation de vigilance URSSAF',
  identite:  "Pièce d'identité du représentant",
  fiscale:   'Attestation de régularité fiscale',
  decennale: 'Attestation Décennale / RC Pro',
};
const DOC_ORDER = ['kbis', 'urssaf', 'identite', 'fiscale', 'decennale'];

const formatSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * Public contract-signature page — no CRM auth required.
 * Accessed via the unique link emailed to the subcontractor.
 */
export default function SignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nom, setNom] = useState('');
  const [qualite, setQualite] = useState('');
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [docsRecent, setDocsRecent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [documents, setDocuments] = useState({}); // { kbis: {...}, urssaf: {...}, ... }
  const [uploadingDoc, setUploadingDoc] = useState(null); // doc_type currently uploading

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const fileInputRefs = useRef({});

  const reload = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/contracts/sign/${token}`);
    setContract(res.data);
    setDocuments(res.data.documents || {});
    if (res.data.status === 'signed') {
      setDone({ signed_pdf_url: res.data.signed_pdf_url, signed_at: res.data.signed_at });
    }
    if (res.data.sous_traitant?.representant_nom) {
      setNom(res.data.sous_traitant.representant_nom);
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (e) {
        setError(e?.response?.data?.detail || 'Lien invalide ou expiré.');
      }
      setLoading(false);
    })();
  }, [reload]);

  // --- Canvas drawing ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a1a3b';
  }, [contract]);

  const getXY = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return {
      x: ((pt.clientX - rect.left) * canvas.width) / rect.width,
      y: ((pt.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const endDraw = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      setError(`Fichier trop lourd (max 12 Mo) : ${file.name}`);
      return;
    }
    setError('');
    setUploadingDoc(docType);
    try {
      const fd = new FormData();
      fd.append('doc_type', docType);
      fd.append('file', file);
      const r = await axios.post(
        `${BACKEND_URL}/api/contracts/sign/${token}/document`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDocuments(d => ({ ...d, [docType]: r.data.document }));
    } catch (e) {
      setError(e?.response?.data?.detail || `Erreur lors de l'envoi du document ${DOC_LABELS[docType]}`);
    }
    setUploadingDoc(null);
  };

  const handleDeleteDoc = async (docType) => {
    if (!window.confirm(`Supprimer le document "${DOC_LABELS[docType]}" ?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/sign/${token}/document/${docType}`);
      setDocuments(d => {
        const nd = { ...d };
        delete nd[docType];
        return nd;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const allDocsUploaded = DOC_ORDER.every(t => documents[t]);

  const handleSubmit = async () => {
    if (!allDocsUploaded) {
      setError('Veuillez joindre les 4 documents obligatoires avant de signer.');
      return;
    }
    if (!docsRecent) {
      setError('Veuillez certifier que les documents fournis datent de moins de 3 mois.');
      return;
    }
    if (!hasInk) { setError('Veuillez dessiner votre signature avant de valider.'); return; }
    if (!nom.trim()) { setError('Nom du signataire requis.'); return; }
    if (!acceptCgu) { setError('Veuillez confirmer votre acceptation.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const res = await axios.post(`${BACKEND_URL}/api/contracts/sign/${token}/sign`, {
        signature_data_url: dataUrl,
        nom_signataire: nom.trim(),
        qualite_signataire: qualite.trim() || 'Représentant légal',
        accepte_cgu: true,
        documents_a_jour: true,
      });
      setDone({ signed_pdf_url: res.data.signed_pdf_url, signed_at: res.data.signed_at });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow border border-slate-200 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Lien invalide</h2>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 max-w-md text-center" data-testid="signed-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Contrat signé !</h2>
          <p className="text-slate-500 text-sm mb-6">
            Votre signature et vos pièces justificatives ont été enregistrées. Une copie du contrat signé vous a été envoyée par email.
          </p>
          {done.signed_pdf_url && (
            <a href={`${BACKEND_URL}${done.signed_pdf_url}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              data-testid="download-signed-pdf">
              <Download className="w-4 h-4" /> Télécharger le contrat signé
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-contract-page">
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white px-6 py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" className="h-10 w-auto bg-white rounded p-1" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <h1 className="font-bold">Contrat de sous-traitance</h1>
              <p className="text-xs text-blue-100">{contract.company.nom} · Référence {contract.contract_id}</p>
            </div>
          </div>
          <ShieldCheck className="w-7 h-7 text-blue-200" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Summary card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase text-slate-400 font-semibold mb-1">Entrepreneur principal</p>
            <p className="font-semibold text-slate-900">{contract.company.nom}</p>
            {contract.company.representant_nom && <p className="text-sm text-slate-500">{contract.company.representant_nom}</p>}
          </div>
          <div>
            <p className="text-xs uppercase text-slate-400 font-semibold mb-1">Sous-traitant</p>
            <p className="font-semibold text-slate-900">{contract.sous_traitant.nom}</p>
            <p className="text-sm text-slate-500">Spécialité : {contract.sous_traitant.specialite}</p>
          </div>
        </div>

        {/* PDF viewer */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="text-slate-700 font-medium">Veuillez lire attentivement le contrat ci-dessous</span>
            {contract.draft_pdf_url && (
              <a href={`${BACKEND_URL}${contract.draft_pdf_url}`} target="_blank" rel="noopener noreferrer"
                className="ml-auto text-xs text-blue-600 hover:underline" data-testid="open-pdf-new-tab">
                Ouvrir dans un nouvel onglet
              </a>
            )}
          </div>
          {contract.draft_pdf_url ? (
            <iframe
              src={`${BACKEND_URL}${contract.draft_pdf_url}`}
              title="Contrat"
              className="w-full"
              style={{ height: '600px', border: 'none' }}
              data-testid="contract-pdf-iframe"
            />
          ) : (
            <div className="p-6 text-center text-slate-400 text-sm">PDF indisponible</div>
          )}
        </div>

        {/* Documents box */}
        <div className="bg-white rounded-xl border-2 border-amber-200 p-5 space-y-3" data-testid="documents-box">
          <div className="flex items-start gap-2">
            <Paperclip className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-bold text-slate-900">Pièces justificatives à joindre</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Avant de signer, veuillez transmettre les 4 documents ci-dessous,
                <b> tous datés de moins de 3 mois</b>. Formats acceptés : PDF, JPG, PNG (12 Mo max par fichier).
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {DOC_ORDER.map(docType => {
              const doc = documents[docType];
              const isUploading = uploadingDoc === docType;
              return (
                <div key={docType}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    doc ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid={`doc-row-${docType}`}
                >
                  <div className="shrink-0">
                    {doc ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{DOC_LABELS[docType]}</p>
                    {doc ? (
                      <p className="text-xs text-slate-500 truncate">
                        {doc.filename} <span className="text-slate-400">· {formatSize(doc.size)}</span>
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400">Aucun fichier joint</p>
                    )}
                  </div>
                  <input
                    ref={el => (fileInputRefs.current[docType] = el)}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*"
                    className="hidden"
                    onChange={e => handleUpload(docType, e.target.files?.[0])}
                    data-testid={`doc-input-${docType}`}
                  />
                  {doc ? (
                    <>
                      <a
                        href={`${BACKEND_URL}${doc.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Voir"
                        data-testid={`doc-view-${docType}`}
                      >
                        <FileText className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteDoc(docType)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer"
                        data-testid={`doc-delete-${docType}`}
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRefs.current[docType]?.click()}
                      disabled={isUploading}
                      data-testid={`doc-upload-${docType}`}
                    >
                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      {isUploading ? 'Envoi…' : 'Joindre'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {!allDocsUploaded && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Les 4 pièces sont obligatoires pour valider la signature électronique du contrat de sous-traitance.</span>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-slate-700 pt-1">
            <input type="checkbox" checked={docsRecent} onChange={(e) => setDocsRecent(e.target.checked)}
              className="mt-1" data-testid="docs-recent-check" />
            <span>
              Je certifie sur l'honneur que les documents transmis ci-dessus sont <b>tous datés de moins de 3 mois</b>
              et conformes à l'original.
            </span>
          </label>
        </div>

        {/* Signature box */}
        <div className="bg-white rounded-xl border-2 border-blue-200 p-5 space-y-4" data-testid="signature-box">
          <h2 className="font-bold text-slate-900">Signature électronique</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom et prénom du signataire *</label>
              <Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Jean Dupont" data-testid="sign-nom" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qualité</label>
              <Input value={qualite} onChange={(e) => setQualite(e.target.value)} placeholder="Gérant, Président, ..." data-testid="sign-qualite" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Signez ci-dessous (souris ou doigt)</label>
              <button onClick={clearCanvas} className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid="sign-clear">
                <Eraser className="w-3 h-3" /> Effacer
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={800}
              height={200}
              className="w-full bg-white border-2 border-dashed border-slate-300 rounded-lg cursor-crosshair touch-none"
              style={{ aspectRatio: '4/1' }}
              onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
              data-testid="sign-canvas"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={acceptCgu} onChange={(e) => setAcceptCgu(e.target.checked)}
              className="mt-1" data-testid="sign-accept" />
            <span>
              Je reconnais avoir lu l'intégralité du contrat de sous-traitance, j'accepte ses termes
              et reconnais que la présente signature manuscrite-électronique a valeur d'engagement
              au sens de l'article 1367 du Code civil. Mon adresse IP et l'horodatage de la signature
              seront enregistrés à titre de preuve.
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button onClick={handleSubmit}
            disabled={submitting || !hasInk || !nom.trim() || !acceptCgu || !allDocsUploaded || !docsRecent}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base font-semibold" data-testid="sign-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            {submitting ? 'Enregistrement…' : 'Signer le contrat'}
          </Button>

          <p className="text-xs text-slate-400 text-center">
            En signant, vous acceptez sans réserve les termes du contrat ci-dessus et joignez vos pièces justificatives.
          </p>
        </div>
      </main>
    </div>
  );
}
